import { shouldLogOutOnForbidden } from '@/lib/api/twoFactorEnrollment';
import {
  CompanyDepartmentCreateRequestBody,
  CompanyDepartmentMoveRequestBody,
  CompanyDepartmentUpdateRequestBody,
} from '@/interfaces/api/general/company';
import { generalApi } from '@/lib/api/general';
import {
  dispatchCheckApiError,
  dispatchLogOut,
  MainContext,
} from '@/store/main/actionsMain';
import {
  commitAddNotification,
  commitSetDepartments,
} from '@/store/main/mutations';
import { defineMessages } from '@/lib/i18n';
import { AxiosError } from 'axios';
import { ApiErrorResponse } from '@/interfaces/api/general/errorResponses';
import sharedMessages from '@/lib/shared-messages';
import { DepartmentCsvRowError } from '@/interfaces/model/general/department';

/**
 * 検証エラーは 400 + 構造化ボディで返る (このとき DB には何も書かれていない)。
 * axios の汎用ハンドラに任せると「入力内容にエラーがあります」に潰れて
 * どの行が原因か分からなくなるので、行エラーをここで取り出して投げ直す。
 */
export class DepartmentCsvValidationError extends Error {
  constructor(public readonly errors: DepartmentCsvRowError[]) {
    super('department csv validation failed');
    this.name = 'DepartmentCsvValidationError';
  }
}

const m = defineMessages({
  companyDepartmentCreated: '部署を作成しました',
  companyDepartmentUpdated: '部署を更新しました',
  companyDepartmentDeleted: '部署を削除しました',
  companyDepartmentMoved: '部署の階層を変更しました',
});

export const actionGetCompanyDepartments = async (
  context: MainContext,
  payload: { companyId: string }
) => {
  try {
    const response = await generalApi.getCompanyDepartments(payload.companyId);
    if (response.data) {
      commitSetDepartments(context, [...response.data]);
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionGetCompanyDepartmentTree = async (
  context: MainContext,
  payload: { companyId: string }
) => {
  try {
    return await generalApi.getCompanyDepartmentTree(payload.companyId);
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionGetCompanyDepartmentDeletionImpact = async (
  context: MainContext,
  payload: { companyId: string; departmentId: string }
) => {
  try {
    return await generalApi.getCompanyDepartmentDeletionImpact(
      payload.companyId,
      payload.departmentId
    );
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionCreateCompanyDepartment = async (
  context: MainContext,
  payload: {
    data: CompanyDepartmentCreateRequestBody;
    companyId: string;
  }
) => {
  try {
    const response = await generalApi.createCompanyDepartment(
      payload.companyId,
      payload.data
    );
    if (response) {
      commitAddNotification(context, {
        messageKey: m.companyDepartmentCreated,
        color: 'success',
      });
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionUpdateCompanyDepartment = async (
  context: MainContext,
  payload: {
    companyId: string;
    departmentId: string;
    data: CompanyDepartmentUpdateRequestBody;
  }
) => {
  try {
    const response = await generalApi.updateCompanyDepartment(
      payload.companyId,
      payload.departmentId,
      payload.data
    );
    if (response) {
      commitAddNotification(context, {
        messageKey: m.companyDepartmentUpdated,
        color: 'success',
      });
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionMoveCompanyDepartment = async (
  context: MainContext,
  payload: {
    companyId: string;
    departmentId: string;
    data: CompanyDepartmentMoveRequestBody;
  }
) => {
  try {
    const response = await generalApi.moveCompanyDepartment(
      payload.companyId,
      payload.departmentId,
      payload.data
    );
    if (response) {
      commitAddNotification(context, {
        messageKey: m.companyDepartmentMoved,
        color: 'success',
      });
    }
    return response;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionDeleteCompanyDepartment = async (
  context: MainContext,
  payload: {
    companyId: string;
    departmentId: string;
  }
) => {
  try {
    const response = await generalApi.deleteCompanyDepartment(
      payload.companyId,
      payload.departmentId
    );
    if (response) {
      commitAddNotification(context, {
        messageKey: m.companyDepartmentDeleted,
        color: 'success',
      });
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionGetCompanyDepartmentsCsv = async (
  context: MainContext,
  payload: { companyId: string; lang?: string }
) => {
  try {
    const response = await generalApi.getCompanyDepartmentsCsv(
      payload.companyId,
      payload.lang
    );
    if (response) {
      commitAddNotification(context, {
        messageKey: sharedMessages.csvDownloadSuccess,
        color: 'success',
      });
    }
    return response;
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};

export const actionPostCompanyDepartmentsCsv = async (
  context: MainContext,
  payload: {
    companyId: string;
    file: File;
    lang?: string;
  }
) => {
  try {
    const formData = new FormData();
    formData.append('file', payload.file);

    const response = await generalApi.postCompanyDepartmentsCsv(
      payload.companyId,
      formData,
      payload.lang
    );

    // 部署一覧を参照している他の画面と食い違わないよう、結果を返す前に更新する
    if (response.data) {
      await actionGetCompanyDepartments(context, {
        companyId: payload.companyId,
      });
    }

    // 件数と移動の内訳は呼び出し側 (結果ダイアログ) が見せる。
    // 移動はフルパスの前後を読ませたいので、20 秒で消えるスナックバーには載せない。
    return response;
  } catch (error) {
    const axiosError = error as AxiosError<
      ApiErrorResponse & { errors?: DepartmentCsvRowError[] }
    >;

    const rowErrors = axiosError?.response?.data?.errors;
    if (
      axiosError?.response?.status === 400 &&
      Array.isArray(rowErrors) &&
      rowErrors.length > 0
    ) {
      throw new DepartmentCsvValidationError(rowErrors);
    }

    await dispatchCheckApiError(context, axiosError);
    if (shouldLogOutOnForbidden(axiosError)) {
      await dispatchLogOut(context);
    }
    throw axiosError;
  }
};
